import { LitElement, css, html, TemplateResult, nothing } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { TraceService, EVENT_CATEGORIES } from "../../../src/services/trace-service";
import { MgLayers, WeatherLayerInfo, LayerGroup } from '../../../src/maplayers/mg-layers';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

// evaluate vessel mode at instance/runtime rather than module-load time so tests
// that set `window.appenv` in a shim before creating elements will work
// (some test bundles may load modules before the global shim runs).
@customElement('voyage-weather-control')
export class VoyageWeatherControl extends LitElement {

    static get styles() {
        return [css`
            :host {
                display: block;
                min-height: 105px;
            }

            input[type="checkbox"] {
                cursor: pointer;
                -webkit-appearance: none;
                appearance: none;
                outline: 0;
                background: white;
                height: 14px;
                width: 14px;
                border-radius: 2.5px;
                border: 1px solid var(--neutrals-grey-06, grey);
                margin: 0;
                margin-top: 5px;
            }

            input[type="checkbox"]:hover {
                filter: brightness(90%);
            }

            input[type="checkbox"]:checked:after {
                content: '';
                position: relative;
                left: 30%;
                top: 20%;
                width: 15%;
                height: 40%;
                border: solid #000;
                border-width: 0 2px 2px 0;
                transform: rotate(45deg);
                display: block;
            }

            .table {
                display: grid;
                grid-template-columns: 160px 1fr 1fr 1fr;
                align-items: center;
                font-family: MaerskText;
                font-size: 12px;
                line-height: 14px;
                color: var(--neutrals-grey-02, black);
                user-select: none;
            }

            .header {
                position: sticky;
                top: 0;
                font-family: MaerskText;
                color: var(--neutrals-grey-02, black);
                background: var(--neutrals-grey-10, white);
                font-weight: bold;
                border-bottom: 1px solid var(--neutrals-grey-08, grey);
            }

            .layer {
                grid-column: 1;
                padding-right: 16px;
            }

            .selector {
                width: 100%;
                text-align: center;
            }

            .group {
                font-weight: bold;
                padding-top: 8px;
            }

            .interactive {
                cursor: pointer;
            }

            .interactive>svg {
                height: 10px;
                fill: currentColor;
            }

            .weather-borders-toggle-container {
                font-family: MaerskText;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 0 0 10px 0;
                border-bottom: 1px solid var(--neutrals-grey-07, grey);
                font-size: 14px;
            }
            .modal-wrapper{
                overflow-y: auto;
                max-height: calc(100vh - 210px);
            }
        `];
    }

    render() {
        let group: LayerGroup;
        return html`
            <div class="modal-wrapper">
                ${  this._isVessel && !isVesselOnCloud
                    ? html`
                        <div class="weather-borders-toggle-container">
                            <span>Show Weather Subscription Areas</span>
                            <input type="checkbox" id="borderLayerToggle" ?checked=${this._borderLayersEnabled}
                                @click=${(evt: InputEvent) => this._onBorderLayerToggle(evt)}>
                        </div>`
                    : nothing }
                <div class="table">
                    ${this._list.map(item => {
                        if (item.group !== group) {
                            group = item.group;
                            return html`${this._renderGroup(group)}${this._renderItem(item)}`;
                        } else {
                            return html`${this._renderItem(item)}`;
                        }
                    })}
                </div>
            </div>
            
        `;
    }

    // evaluate vessel mode at runtime (reads global shim during tests)
    private get _isVessel(): boolean {
        return (globalThis as any).appenv?.vesselIMONumber !== undefined;
    }

    firstUpdated() {
        this._borderLayersEnabled = MgLayers!.borderLayersEnabled;
        MgLayers!.addEventListener('change', () => {
            this._list = MgLayers!.list;
        });
        this._list = MgLayers!.list;
    }

    @property({ attribute: false }) private _list: WeatherLayerInfo[] = [];
    @property({ attribute: false }) private _groupExpanded: Map<LayerGroup, boolean> = new Map([
        [LayerGroup.Wind, true],
        [LayerGroup.SeaAndSwell, true],
        [LayerGroup.PressureAndTemp, true],
        [LayerGroup.Others, true]
    ]);
    private _borderLayersEnabled: boolean = false;

    private _renderGroup(layerGroup: LayerGroup): TemplateResult {
        const first = layerGroup === LayerGroup.Wind;
        let group: string;
        switch (layerGroup) {
            case LayerGroup.Wind:
                group = "Wind";
                break;

            case LayerGroup.SeaAndSwell:
                group = "Sea & Swell";
                break;

            case LayerGroup.PressureAndTemp:
                group = "Pressure & Temp.";
                break;

            case LayerGroup.Others:
                group = "Other Layers";
                break;
        }
        return html`
            <div class="group interactive" @click=${() => this._onGroupClick(layerGroup)}>
                ${this._groupExpanded.get(layerGroup) ? SvgIcons.chevronDown : SvgIcons.chevronUp}
                ${group}
            </div>
            ${first ? html`
            <div class="group selector">Grid</div>
            <div class="group selector">Isoline</div>
            <div class="group selector">Area</div>
            ` : html`
            <div></div>
            <div></div>
            <div></div>
            `}
        `;
    }

    private _renderItem(item: WeatherLayerInfo): TemplateResult {
        const hidden = !this._groupExpanded.get(item.group);
        return html`<div class="layer" ?hidden=${hidden}>${item.name + " " + this._handleUnitText(item.element)}</div>
        ${item.nongrid
                ? html`
                <div class="selector" ?hidden=${hidden}><input type="checkbox" data-test=${"grid" + item.element} ?checked=${item.nongrid.visible}
                    @click=${(e: Event) => this._onCheck(e, item, 'nongrid')}></div>
                <div></div>
                <div></div>
                `
                : html`
                <div class="selector" ?hidden=${hidden}><input type="checkbox" data-test=${"grid" + item.element} ?checked=${item.grid!.visible}
                    @click=${(e: Event) => this._onCheck(e, item, 'grid')}></div>
                <div class="selector" ?hidden=${hidden}><input type="checkbox" data-test=${"isoline" + item.element} ?checked=${item.line!.visible}
                    @click=${(e: Event) => this._onCheck(e, item, 'line')}></div>
                <div class="selector" ?hidden=${hidden}><input type="checkbox" data-test=${"area" + item.element} ?checked=${item.area!.visible}
                    @click=${(e: Event) => this._onCheck(e, item, 'area')}></div>`}`
    }

    private _handleUnitText(element: string): string {
        return ["Wind10M", "Wind50M", "Currents", "WindGusts50M", "WindGusts10M", "RiskWindSpeed"]
        .includes(element) ? "(kt)" : "";
    }

    private _onGroupClick(group: LayerGroup) {
        console.log(group);
        this._groupExpanded.set(group, !this._groupExpanded.get(group));
        this.requestUpdate();
    }

    private _onCheck(evt: Event, item: WeatherLayerInfo, type: string) {
        const checked = (<HTMLInputElement>evt.target).checked;
        TraceService!.event(EVENT_CATEGORIES.WEATHER_CONTROL, checked ? 'check' : 'uncheck', `${item.element}_${type}`);
        MgLayers!.setVisible(item.element, type, checked);
    }


    private _onBorderLayerToggle(evt: Event) {
        const checked = (<HTMLInputElement>evt.target).checked;
        MgLayers!.toggleBorderLayers(checked);
        this._borderLayersEnabled = checked;
    }
};
